import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useWizardBackNav } from '../../../hooks/useWizardBackNav';

import {
  useGetServerOptionsQuery,
  useGetServerNextOptionsQuery,
  useGetServer4ParentProxiesQuery,
  useGetServer4ProvidersQuery,
  useGetOrderSummaryMutation,
  usePurchaseProxyMutation,
  useCheckProxy5gUsernameQuery,
  useLazyCheckProxy5gUsernameQuery,
} from '../../../store/api/proxiesApi';
import {
  ProxyOrderSummaryData,
  PurchaseProxyRequestServer4,
  Server4Country,
  Server4Options,
  Server4Package,
  Server4Price,
  Server4City,
  Server4ParentProxy,
  Server4Provider,
} from '../../../types/api';
import { ButtonSpinner, Loader } from '../../ui/Loader';

const SERVER_ID = 4;

type WizardStep = 'package' | 'protocol' | 'country' | 'city' | 'serviceProvider' | 'parentProxy' | 'config' | 'review';

const pkgDisplayNames: Record<string, string> = { '1': 'Premium Package', '2': 'Basic Package', '3': 'Premium Package Injection', '4': 'Ultimate Package', '5': 'Ultimate Package Injection' };
const getPkgDisplayName = (pkg: Server4Package): string => pkgDisplayNames[String(pkg.id)] || pkg.name;

function formatDurationLabel(value: number | null, isHours: boolean, t: (key: string) => string): string {
  if (value == null || value <= 0) return t('proxies.wizard.unknown');
  if (isHours) {
    if (value === 1) return t('proxies.wizard.hour');
    if (value === 2) return t('proxies.wizard.twoHours');
    return `${value} ${t('proxies.wizard.hours')}`;
  }
  if (value === 1) return t('proxies.wizard.day');
  if (value === 2) return t('proxies.wizard.twoDays');
  return `${value} ${t('proxies.wizard.days')}`;
}

export function Server4Wizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<WizardStep>('package');
  const [selectedPkg, setSelectedPkg] = useState<string>('');
  const [countrySearch, setCountrySearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [citySearch, setCitySearch] = useState('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedParentProxy, setSelectedParentProxy] = useState<string>('');
  const [selectedServiceProvider, setSelectedServiceProvider] = useState<string>('');
  const [protocol, setProtocol] = useState<string>('http');
  const [duration, setDuration] = useState<number>(30);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [usernameToCheck, setUsernameToCheck] = useState<string>('');
  const [ipAddresses, setIpAddresses] = useState<string[]>(['']);
  const [error, setError] = useState<string | null>(null);
  const [isUsernameError, setIsUsernameError] = useState(false);
  const [summary, setSummary] = useState<ProxyOrderSummaryData | null>(null);

  const { data: optionsData, isLoading: loadingOptions, isError, error: queryError } = useGetServerOptionsQuery(SERVER_ID);
  const [purchaseProxy, { isLoading: isPurchasing }] = usePurchaseProxyMutation();
  const [getOrderSummary, { isLoading: loadingSummary }] = useGetOrderSummaryMutation();
  const usernameRef = useRef<HTMLInputElement>(null);
  const [triggerCheckUsername] = useLazyCheckProxy5gUsernameQuery();

  const { data: nextOptionsData, isLoading: loadingNextOptions } =
    useGetServerNextOptionsQuery(
      {
        serverId: SERVER_ID,
        params: { pkgId: selectedPkg, countryId: selectedCountry },
      },
      { skip: !selectedPkg || !selectedCountry || step === 'package' || step === 'protocol' || step === 'country' }
    );

  const { data: providersData, isLoading: loadingProviders } =
    useGetServer4ProvidersQuery(
      { serverId: SERVER_ID, cityId: Number(selectedCity), pkgId: Number(selectedPkg) },
      { skip: !selectedPkg || !selectedCity || step === 'package' || step === 'protocol' || step === 'country' || step === 'config' || step === 'review' }
    );

  const { data: parentProxiesData, isLoading: loadingParentProxies } =
    useGetServer4ParentProxiesQuery(
      { pkgId: Number(selectedPkg), countryId: Number(selectedCountry), cityId: selectedCity ? Number(selectedCity) : undefined, serviceProviderCityId: selectedServiceProvider ? Number(selectedServiceProvider) : undefined },
      { skip: !selectedPkg || !selectedCountry }
    );

  const { data: usernameCheckData, isFetching: checkingUsername } =
    useCheckProxy5gUsernameQuery(
      { username: usernameToCheck, pkgId: Number(selectedPkg) },
      { skip: !usernameToCheck || !selectedPkg || step !== 'config' }
    );

  const usernameChecked = !!usernameToCheck && !checkingUsername && usernameCheckData !== undefined;
  const result = usernameCheckData?.data?.result;
  const usernameAvailable = usernameChecked && result !== 'ALREADY_USER' && result !== 'ALREADY_USED';
  const usernameTaken = usernameChecked && !usernameAvailable;
  const usernameChecking = checkingUsername;
  const isInjectionPackage = Number(selectedPkg) === 3 || Number(selectedPkg) === 5;

  const options = optionsData as Server4Options | undefined;
  const optionsPayload = (options as { data?: Server4Options })?.data ?? options;

  const packages: Server4Package[] = [...(optionsPayload?.options?.packages ?? [])].sort((a, b) => {
    const order = [2, 1, 3, 4, 5]; // Basic (2) → Premium (1) → Premium Injection (3) → Ultimate (4) → Ultimate Injection (5)
    return order.indexOf(Number(a.id)) - order.indexOf(Number(b.id));
  });
  const allCountries: Server4Country[] = optionsPayload?.options?.countries ?? [];
  const packageCountriesMap = optionsPayload?.options?.packageCountries;
  const countries: Server4Country[] = (selectedPkg && packageCountriesMap)
    ? (packageCountriesMap[selectedPkg] ?? allCountries)
    : allCountries;
  const profitMargin = optionsPayload?.options?.profitMargin ?? 0;
  const allPrices: Server4Price[] = [...(optionsPayload?.options?.prices ?? [])].sort((a, b) => {
    const daysDiff = Number(a.days) - Number(b.days);
    if (daysDiff !== 0) return daysDiff;
    return Number(a.hours) - Number(b.hours);
  });
  const prices = selectedPkg ? allPrices.filter((p) => String(p.pkgId) === selectedPkg) : allPrices;

  const filteredCountries = countrySearch.trim()
    ? countries.filter(
        (c) =>
          c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
          String(c.id).toLowerCase().includes(countrySearch.toLowerCase())
      )
    : countries;

  const nextOptionsPayload = nextOptionsData as { data?: { nextOptions?: { cities?: Server4City[] } } } | undefined;
  const citiesPayload = nextOptionsPayload?.data ?? nextOptionsPayload;
  const cities: Server4City[] = (citiesPayload as { nextOptions?: { cities?: Server4City[] } })?.nextOptions?.cities ?? [];

  const filteredCities = citySearch.trim()
    ? cities.filter(
        (c) =>
          c.name.toLowerCase().includes(citySearch.toLowerCase()) ||
          String(c.id).toLowerCase().includes(citySearch.toLowerCase())
      )
    : cities;

  const rawParentProxies = ((parentProxiesData as { data?: { parentProxies?: Server4ParentProxy[] } } | undefined)?.data?.parentProxies ?? []);

  const parentProxies: Server4ParentProxy[] = rawParentProxies.filter((p) => {
    if (protocol === 'socks') return !!p.socksPort;
    return !!p.httpPort;
  });

  const providersPayload = providersData as { data?: { providers?: Server4Provider[] } } | undefined;
  const providers: Server4Provider[] = providersPayload?.data?.providers ?? [];

  const selectedPrice = prices.find((p) => {
    if (Number(duration) >= 1) return Number(p.days) === Number(duration);
    return p.hours && Number(p.hours) === Math.round(Number(duration) * 100);
  });

  // Fetch order summary when on review step
  useEffect(() => {
    if (step !== 'review') return;
    if (!selectedPkg || !selectedParentProxy || !protocol || !duration) return;
    setError(null);
    const body = {
      pkgId: Number(selectedPkg),
      countryId: selectedCountry ? Number(selectedCountry) : undefined,
      cityId: selectedCity ? Number(selectedCity) : undefined,
      parentProxyId: selectedParentProxy,
      serviceProviderCityId: selectedServiceProvider ? Number(selectedServiceProvider) : undefined,
      protocol,
      duration,
    };
    getOrderSummary({ serverId: 4, body })
      .unwrap()
      .then((res) => setSummary(res.data))
      .catch((err: unknown) => {
        const message =
          (err as { data?: { message?: string }; message?: string })?.data?.message ||
          (err as { message?: string })?.message ||
          t('proxies.purchase.error');
        setError(message);
        setSummary(null);
      });
  }, [step, selectedPkg, selectedCountry, selectedCity, selectedParentProxy, protocol, duration]);

  const handlePackageSelect = (pkgId: string) => {
    setSelectedPkg(pkgId);
    setError(null);
    setUsername('');
    setPassword('');
    setUsernameToCheck('');
    setIpAddresses(['']);
    if (Number(pkgId) === 3 || Number(pkgId) === 5) {
      setProtocol('http');
      setStep('country');
    } else {
      setStep('protocol');
    }
  };

  const handleProtocolSelect = (proto: string) => {
    setProtocol(proto);
    setSelectedParentProxy('');
    setError(null);
    setStep('country');
  };

  const handleCountrySelect = (countryId: string) => {
    setSelectedCountry(countryId);
    setError(null);
    setStep('city');
  };

  const handleServiceProviderSelect = (providerId: string) => {
    setSelectedServiceProvider(providerId);
    setSelectedParentProxy('');
    setError(null);
    setStep('parentProxy');
  };

  const handleCitySelect = (cityId: string) => {
    setSelectedCity(cityId);
    setSelectedServiceProvider('');
    setSelectedParentProxy('');
    setError(null);
    setStep('serviceProvider');
  };

  const handleIpChange = (index: number, value: string) => {
    const newIps = [...ipAddresses];
    newIps[index] = value;
    setIpAddresses(newIps);
  };

  const restoreStep = useCallback((targetStep: WizardStep) => {
    if (targetStep === 'package') {
      setSelectedPkg('');
    } else if (targetStep === 'protocol') {
      // No downstream state to clear; protocol selection handles parentProxy reset
    } else if (targetStep === 'country') {
      setSelectedCountry('');
      setCitySearch('');
    } else if (targetStep === 'city') {
      setSelectedCity('');
      setSelectedServiceProvider('');
      setSelectedParentProxy('');
    } else if (targetStep === 'serviceProvider') {
      setSelectedServiceProvider('');
      setSelectedParentProxy('');
    } else if (targetStep === 'config') {
      setSummary(null);
    }
    setStep(targetStep);
  }, []);

  const { goBack: handleBack } = useWizardBackNav(step, restoreStep, 'server4-purchase', true, 'package');

  // Auto-advance past serviceProvider if no providers exist
  useEffect(() => {
    if (step === 'serviceProvider' && !loadingProviders && providers.length === 0) {
      setStep('parentProxy');
    }
  }, [step, loadingProviders, providers]);

  const handleParentProxySelect = (proxyId: string) => {
    setSelectedParentProxy(proxyId);
    setError(null);
    setStep('config');
  };

  const handleNext = () => {
    setError(null);
    if (step === 'config') {
      if (duration == null) {
        setError(t('proxies.wizard.errors.requiredFields'));
        return;
      }
      if (isInjectionPackage) {
        const validIps = ipAddresses.filter((ip) => {
          const trimmed = ip.trim();
          return trimmed && /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(trimmed);
        });
        if (validIps.length === 0) {
          setError(t('proxies.wizard.server4.atLeastOneIp') || 'At least one valid IP address is required');
          return;
        }
      } else {
        if (!username.trim() || !password.trim()) {
          setError(t('proxies.wizard.errors.requiredFields'));
          return;
        }
        if (username.trim().length < 4 || username.trim().length > 20 || !/^[a-z0-9]+$/.test(username.trim())) {
          setError(t('proxies.wizard.server4.usernameInvalid') || '4-20 chars, lowercase letters, numbers');
          return;
        }
        if (password.trim().length < 6 || password.trim().length > 30 || !/^[a-z0-9]+$/.test(password.trim())) {
          setError(t('proxies.wizard.server4.passwordInvalid') || '6-30 chars, lowercase letters, numbers');
          return;
        }
        if (username.trim() && !usernameChecked) {
          setUsernameToCheck(username.trim());
          setError(t('proxies.wizard.server4.checkUsernameFirst') || 'Check username availability first');
          return;
        }
        if (usernameChecked && !usernameAvailable) {
          setError(t('proxies.wizard.server4.usernameTakenError') || 'Please choose a different username');
          return;
        }
      }
      setStep('review');
    }
  };

  const handlePurchase = async () => {
    setError(null);
    setIsUsernameError(false);

    if (!isInjectionPackage) {
      try {
        const checkResult = await triggerCheckUsername({
          username: username.trim(),
          pkgId: Number(selectedPkg),
        });

        if (checkResult.data?.data?.result === 'ALREADY_USER' || checkResult.data?.data?.result === 'ALREADY_USED') {
          setIsUsernameError(true);
          setStep('config');
          setUsername('');
          setUsernameToCheck('');
          setError(t('proxies.wizard.server4.usernameTakenAtPurchase'));
          toast.error(t('proxies.wizard.server4.usernameTakenAtPurchase'));
          return;
        }
      } catch {
        setIsUsernameError(true);
        setStep('config');
        setError(t('proxies.wizard.server4.usernameCheckFailed') || 'Could not verify username. Please try again.');
        toast.error(t('proxies.wizard.server4.usernameCheckFailed') || 'Could not verify username. Please try again.');
        return;
      }
    }

    try {
      const body: PurchaseProxyRequestServer4 = {
        serverId: 4,
        parentProxyId: selectedParentProxy,
        protocol,
        duration,
        pkgId: selectedPkg ? Number(selectedPkg) : undefined,
        countryId: selectedCountry ? Number(selectedCountry) : undefined,
        cityId: selectedCity ? Number(selectedCity) : undefined,
        serviceProviderCityId: selectedServiceProvider ? Number(selectedServiceProvider) : undefined,
      };

      if (isInjectionPackage) {
        const validIp = ipAddresses
          .map((ip) => ip.trim())
          .find((ip) => ip && /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ip));
        if (validIp) body.ipAddr = validIp;
      } else {
        body.username = username.trim();
        body.password = password.trim();
      }

      const response = await purchaseProxy(body).unwrap();

      toast.success(t('proxies.purchase.success'));
      if (response.data.proxies && response.data.proxies.length > 0 && response.data.proxies[0]) {
        navigate(`/proxies/${response.data.proxies[0]._id}`);
      } else {
        navigate('/proxies/list');
      }
    } catch (err: unknown) {
      const message =
        (err as { data?: { message?: string }; message?: string })?.data?.message ||
        (err as { message?: string })?.message ||
        t('proxies.purchase.error');
      setError(message);
      toast.error(message);
    }
  };

  useEffect(() => {
    if (step === 'config' && isUsernameError) {
      usernameRef.current?.focus();
    } else if (step !== 'config') {
      setIsUsernameError(false);
    }
  }, [step, isUsernameError]);

  if (loadingOptions) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader />
      </div>
    );
  }

  if (isError) {
    const errorMsg =
      (queryError as { data?: { message?: string } })?.data?.message ||
      (queryError as { message?: string })?.message ||
      t('proxies.purchase.error');
    return (
      <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/20 mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <p className="text-xl font-heading font-semibold text-red-400 mb-2">
          {t('proxies.purchase.error')}
        </p>
        <p className="text-neutral-medium font-body text-sm">{errorMsg}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {['package', 'protocol', 'country', 'city', 'serviceProvider', 'config', 'review'].map((s, idx) => {
            const stepNames = ['package', 'protocol', 'country', 'city', 'serviceProvider', 'parentProxy', 'config', 'review'];
            const currentIdx = stepNames.indexOf(step);
            const thisIdx = stepNames.indexOf(s);
            const isActive = thisIdx === currentIdx;
            const isPast = thisIdx < currentIdx;
            return (
              <div key={s} className="flex items-center">
                {idx > 0 && (
                  <div className={`flex-1 h-0.5 mx-2 ${isPast || isActive ? 'bg-brand-accent' : 'bg-neutral-dark'}`} style={{ width: '2rem' }} />
                )}
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center border-2 text-xs font-bold ${
                    isActive
                      ? 'border-brand-accent bg-brand-accent/20 text-brand-accent'
                      : isPast
                        ? 'border-green-500 bg-green-500/20 text-green-500'
                        : 'border-neutral-medium text-neutral-medium'
                  }`}
                >
                  {idx + 1}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Step 1: Select Package */}
      {step === 'package' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server4.selectPackage') || 'Select Package'}
          </h2>
          <div className="space-y-4">
            {/* Basic Package (alone) */}
            {packages.filter(p => Number(p.id) === 2).map((pkg) => (
              <button
                key={pkg.id}
                onClick={() => handlePackageSelect(String(pkg.id))}
                className="w-full p-4 bg-neutral-dark rounded-lg border border-neutral-dark hover:border-brand-accent/50 transition-all text-left"
              >
                <h3 className="font-heading font-semibold text-neutral-light">{getPkgDisplayName(pkg)}</h3>
              </button>
            ))}
            
            {/* Premium & Premium Injection (side by side) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {packages.filter(p => Number(p.id) === 1 || Number(p.id) === 3).map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => handlePackageSelect(String(pkg.id))}
                  className="p-4 bg-neutral-dark rounded-lg border border-neutral-dark hover:border-brand-accent/50 transition-all text-left"
                >
                  <h3 className="font-heading font-semibold text-neutral-light">{getPkgDisplayName(pkg)}</h3>
                </button>
              ))}
            </div>
            
            {/* Ultimate & Ultimate Injection (side by side) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {packages.filter(p => Number(p.id) === 4 || Number(p.id) === 5).map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => handlePackageSelect(String(pkg.id))}
                  className="p-4 bg-neutral-dark rounded-lg border border-neutral-dark hover:border-brand-accent/50 transition-all text-left"
                >
                  <h3 className="font-heading font-semibold text-neutral-light">{getPkgDisplayName(pkg)}</h3>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Select Protocol */}
      {step === 'protocol' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server4.selectProtocol') || 'Select Protocol'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => handleProtocolSelect('http')}
              className={`p-6 rounded-lg border-2 transition-all text-left ${
                protocol === 'http'
                  ? 'border-brand-accent bg-brand-accent/20'
                  : 'border-neutral-dark bg-neutral-dark hover:border-brand-accent/50'
              }`}
            >
              <h3 className="font-heading font-semibold text-neutral-light text-lg">HTTP</h3>
              <p className="text-neutral-medium text-sm font-body mt-1">
                {t('proxies.wizard.server4.httpDescription') || 'Standard HTTP/HTTPS proxy'}
              </p>
            </button>
            <button
              onClick={() => handleProtocolSelect('socks')}
              className={`p-6 rounded-lg border-2 transition-all text-left ${
                protocol === 'socks'
                  ? 'border-brand-accent bg-brand-accent/20'
                  : 'border-neutral-dark bg-neutral-dark hover:border-brand-accent/50'
              }`}
            >
              <h3 className="font-heading font-semibold text-neutral-light text-lg">SOCKS</h3>
              <p className="text-neutral-medium text-sm font-body mt-1">
                {t('proxies.wizard.server4.socksDescription') || 'SOCKS5 proxy for wider compatibility'}
              </p>
            </button>
          </div>
          <button
            onClick={handleBack}
            className="mt-6 w-full px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body"
          >
            {t('common.back')}
          </button>
        </div>
      )}

      {/* Step 3: Select Country */}
      {step === 'country' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server4.selectCountry') || 'Select Country'}
          </h2>
          <div className="space-y-6">
            <div className="border border-neutral-dark rounded-lg bg-neutral-dark overflow-hidden">
              <div className="p-2 border-b border-neutral-dark sticky top-0 bg-neutral-dark">
                <input
                  type="text"
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  placeholder={t('proxies.wizard.server4.searchCountry') || 'Search country...'}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded text-neutral-light font-body placeholder-neutral-medium"
                />
              </div>
              <ul className="max-h-80 overflow-y-auto py-1 bg-neutral-dark">
                {filteredCountries.map((country) => (
                  <li key={country.id}>
                    <button
                      type="button"
                      onClick={() => handleCountrySelect(String(country.id))}
                      className="w-full px-4 py-3 text-left font-body text-neutral-light hover:bg-brand-accent/20 border-b border-neutral-dark/50 last:border-0 transition-colors flex justify-between items-center"
                    >
                      <span className="font-medium">{country.name}</span>
                      <span className="text-neutral-medium text-sm">{country.id}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <button
              onClick={handleBack}
              className="w-full px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body"
            >
              {t('common.back')}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Select City */}
      {step === 'city' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server4.selectCity') || 'Select City'}
          </h2>
          {loadingNextOptions ? (
            <div className="flex justify-center py-12"><Loader /></div>
          ) : (
            <div className="space-y-6">
              <div className="border border-neutral-dark rounded-lg bg-neutral-dark overflow-hidden">
                <div className="p-2 border-b border-neutral-dark sticky top-0 bg-neutral-dark">
                  <input
                    type="text"
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    placeholder={t('proxies.wizard.server4.searchCity') || 'Search city...'}
                    className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded text-neutral-light font-body placeholder-neutral-medium"
                  />
                </div>
                <ul className="max-h-80 overflow-y-auto py-1 bg-neutral-dark">
                  {filteredCities.map((city) => (
                    <li key={city.id}>
                      <button
                        type="button"
                        onClick={() => handleCitySelect(String(city.id))}
                        className="w-full px-4 py-3 text-left font-body text-neutral-light hover:bg-brand-accent/20 border-b border-neutral-dark/50 last:border-0 transition-colors flex justify-between items-center"
                      >
                        <span className="font-medium">{city.name}</span>
                        <span className="text-neutral-medium text-sm">{city.id}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={handleBack}
                className="w-full px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body"
              >
                {t('common.back')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 5: Select Service Provider */}
      {step === 'serviceProvider' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server4.selectServiceProvider') || 'Select Service Provider'}
          </h2>
          {loadingProviders ? (
            <div className="flex justify-center py-12"><Loader /></div>
          ) : providers.length > 0 ? (
            <div className="space-y-6">
              <ul className="max-h-80 overflow-y-auto py-1 bg-neutral-dark rounded-lg">
                {providers.map((provider) => (
                  <li key={provider.id}>
                    <button
                      type="button"
                      onClick={() => handleServiceProviderSelect(String(provider.id))}
                      className="w-full px-4 py-3 text-left font-body text-neutral-light hover:bg-brand-accent/20 border-b border-neutral-dark/50 last:border-0 transition-colors flex justify-between items-center"
                    >
                      <span className="font-medium">{provider.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <button
                onClick={handleBack}
                className="w-full px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body"
              >
                {t('common.back')}
              </button>
            </div>
          ) : null}
        </div>
      )}

      {/* Step 6: Select Parent Proxy */}
      {step === 'parentProxy' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server4.selectParentProxy') || 'Select Parent Proxy'}
          </h2>
          {loadingParentProxies ? (
            <div className="flex justify-center py-12"><Loader /></div>
          ) : parentProxies.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-neutral-medium font-body">{t('proxies.wizard.server4.noParentProxies') || 'No parent proxies available'}</p>
              <button
                onClick={handleBack}
                className="mt-4 px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body"
              >
                {t('common.back')}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto pr-2">
                {parentProxies.map((pp) => (
                  <button
                    key={pp.id}
                    type="button"
                    onClick={() => handleParentProxySelect(String(pp.id))}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      String(pp.id) === selectedParentProxy
                        ? 'border-brand-accent bg-brand-accent/20'
                        : 'border-neutral-dark bg-neutral-dark hover:border-brand-accent/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-neutral-light font-heading font-semibold text-lg">{pp.ip || '-'}</span>
                    </div>
                    <div className="space-y-1 text-sm font-body">
                      <div className="flex justify-between">
                        <span className="text-neutral-medium">HTTP Port:</span>
                        <span className="text-neutral-light font-mono">{pp.httpPort ?? '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-medium">SOCKS Port:</span>
                        <span className="text-neutral-light font-mono">{pp.socksPort ?? '-'}</span>
                      </div>
                      {pp.rotationTime && (
                        <div className="flex justify-between">
                          <span className="text-neutral-medium">Rotation:</span>
                          <span className="text-neutral-light">{pp.rotationTime}</span>
                        </div>
                      )}
                      {pp.provider && (
                        <div className="flex justify-between">
                          <span className="text-neutral-medium">Provider:</span>
                          <span className="text-neutral-light">{pp.provider}</span>
                        </div>
                      )}
                      {pp.city && (
                        <div className="flex justify-between">
                          <span className="text-neutral-medium">City:</span>
                          <span className="text-neutral-light">{pp.city}</span>
                        </div>
                      )}
                      {pp.usage && (
                        <div className="flex justify-between">
                          <span className="text-neutral-medium">Usage:</span>
                          <span className="text-neutral-light">{pp.usage}</span>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              <button
                onClick={handleBack}
                className="w-full px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body"
              >
                {t('common.back')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 5: Configure */}
      {step === 'config' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server4.configure') || 'Configure Proxy'}
          </h2>
          <div className="space-y-6">
            {isInjectionPackage ? (
              <div>
                <label className="block text-neutral-light font-body mb-2">
                  {t('proxies.wizard.server4.ipAddress') || 'IP Address'}
                </label>
                <input
                  type="text"
                  value={ipAddresses[0] || ''}
                  onChange={(e) => handleIpChange(0, e.target.value)}
                  placeholder="192.168.1.1"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body placeholder-neutral-medium"
                />
                <p className="text-neutral-medium text-xs font-body mt-1">
                  {t('proxies.wizard.server4.ipAddressHint') || 'Enter a valid IP address (e.g., 192.168.1.1)'}
                </p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-neutral-light font-body mb-2">
                    {t('proxies.wizard.server4.username') || 'Username'}
                  </label>
                  <div className="flex gap-2">
                    <input
                      ref={usernameRef}
                      type="text"
                      value={username}
                      onChange={(e) => { setUsername(e.target.value); setUsernameToCheck(''); setIsUsernameError(false); }}
                      onBlur={() => { if (username.trim()) setUsernameToCheck(username.trim()); }}
                      placeholder={t('proxies.wizard.server4.usernamePlaceholder') || 'Choose a username'}
                      className={`flex-1 px-4 py-2 bg-neutral-dark border rounded-lg text-neutral-light font-body placeholder-neutral-medium ${isUsernameError ? 'border-red-500' : 'border-neutral-dark'}`}
                    />
                  </div>
                  {usernameToCheck && (
                    <div className="mt-1 text-sm font-body">
                      {checkingUsername ? (
                        <span className="text-neutral-medium">{t('common.checking')}...</span>
                      ) : !usernameChecked ? (
                        <span className="text-neutral-medium">{t('common.checking')}...</span>
                      ) : usernameAvailable ? (
                        <span className="text-green-400">✅ {t('proxies.wizard.server4.usernameAvailable') || 'Username available'}</span>
                      ) : (
                        <span className="text-red-400">❌ {usernameCheckData?.data?.message || t('proxies.wizard.server4.usernameTaken') || 'Username taken'}</span>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-neutral-light font-body mb-2">
                    {t('proxies.wizard.server4.password') || 'Password'}
                  </label>
                  <input
                    type="text"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('proxies.wizard.server4.passwordPlaceholder') || 'Enter password'}
                    className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body placeholder-neutral-medium"
                  />
                </div>
              </>
            )}

            {/* Duration */}
            <div>
              <label className="block text-neutral-light font-body mb-2">
                {t('proxies.wizard.server4.duration') || 'Duration (days)'}
              </label>
              <select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
              >
                {prices.map((p) => {
                  const displayPrice = Number(p.price) * (1 + profitMargin);
                  const days = p.days != null ? Number(p.days) : null;
                  const hours = p.hours != null ? Number(p.hours) : null;
                  const isHours = (days == null || days < 1) && hours != null && hours > 0;
                  const optValue = isHours ? (hours / 100) : (days ?? 0);
                  const icon = isHours ? '⏰' : '📅';
                  const label = `${icon} ${formatDurationLabel(isHours ? hours : days, isHours, t)} - $${displayPrice.toFixed(2)}`;
                  return (
                    <option key={isHours ? `h${hours}` : `d${days}`} value={optValue}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>

            {error && (
              <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 font-body">
                {error}
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={handleBack}
                className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body"
              >
                {t('common.back')}
              </button>
              <button
                onClick={handleNext}
                className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all font-body font-semibold"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 6: Review */}
      {step === 'review' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.review')}
          </h2>
          <div className="space-y-4 mb-6">
            <div className="flex justify-between">
              <span className="text-neutral-medium font-body">{t('proxies.wizard.server4.package') || 'Package'}:</span>
              <span className="text-neutral-light font-body">{(pkgDisplayNames[selectedPkg] || packages.find((p) => String(p.id) === selectedPkg)?.name) ?? selectedPkg}</span>
            </div>
            {selectedCountry && (
              <div className="flex justify-between">
                <span className="text-neutral-medium font-body">{t('proxies.wizard.server4.country') || 'Country'}:</span>
                <span className="text-neutral-light font-body">{countries.find((c) => String(c.id) === selectedCountry)?.name ?? selectedCountry}</span>
              </div>
            )}
            {selectedCity && (
              <div className="flex justify-between">
                <span className="text-neutral-medium font-body">{t('proxies.wizard.server4.city') || 'City'}:</span>
                <span className="text-neutral-light font-body">{cities.find((c) => String(c.id) === selectedCity)?.name ?? selectedCity}</span>
              </div>
            )}
            {selectedServiceProvider && (
              <div className="flex justify-between">
                <span className="text-neutral-medium font-body">{t('proxies.wizard.server4.serviceProvider') || 'Service Provider'}:</span>
                <span className="text-neutral-light font-body">{providers.find((p) => String(p.id) === selectedServiceProvider)?.name ?? selectedServiceProvider}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-neutral-medium font-body">{t('proxies.wizard.server4.protocol') || 'Protocol'}:</span>
              <span className="text-neutral-light font-body">{protocol.toUpperCase()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-medium font-body">{t('proxies.wizard.server4.duration') || 'Duration'}:</span>
              <span className="text-neutral-light font-body">
                {formatDurationLabel(
                  duration >= 1 ? duration : Math.round(duration * 100),
                  duration < 1,
                  t,
                )}
              </span>
            </div>
            {isInjectionPackage ? (
              <div className="flex justify-between">
                <span className="text-neutral-medium font-body">{t('proxies.wizard.server4.ipAddress') || 'IP Address'}:</span>
                <span className="text-neutral-light font-body">{ipAddresses[0]?.trim() || '-'}</span>
              </div>
            ) : username ? (
              <>
                <div className="flex justify-between">
                  <span className="text-neutral-medium font-body">{t('proxies.wizard.server4.username') || 'Username'}:</span>
                  <span className="text-neutral-light font-body">{username}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-medium font-body">{t('proxies.wizard.server4.password') || 'Password'}:</span>
                  <span className="text-neutral-light font-body">{'•'.repeat(Math.min(password.length, 20))}</span>
                </div>
              </>
            ) : null}
            {loadingSummary && (
              <div className="flex items-center justify-center py-2">
                <ButtonSpinner size="sm" className="text-brand-accent" />
              </div>
            )}
            {summary && !loadingSummary && (
              <div className="pt-4 mt-4 border-t border-neutral-dark space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-neutral-medium font-body">
                    {t('proxies.wizard.server1.finalPrice')}
                    {summary.isEstimate && (
                      <span className="ml-2 text-sm text-amber-400">
                        ({t('proxies.wizard.server1.priceEstimate')})
                      </span>
                    )}
                  </span>
                  <span className="text-neutral-light font-body font-semibold">
                    {(() => {
                      const finalPrice = Number(summary.finalPrice);
                      return Number.isFinite(finalPrice) ? finalPrice.toFixed(2) : String(summary.finalPrice);
                    })()} {summary.currency}
                  </span>
                </div>
              </div>
            )}
            {/* Important Notes */}
            <div className="mt-6 pt-4 border-t border-neutral-dark">
              <div className="text-neutral-light font-body whitespace-pre-line">
                {t('proxies.proxy5gImportantNotes')}
              </div>
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 font-body mb-6">
              {error}
            </div>
          )}

          <div className="flex gap-4">
            <button
              onClick={handleBack}
              disabled={isPurchasing || loadingSummary}
              className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body disabled:opacity-50"
            >
              {t('common.back')}
            </button>
            <button
              onClick={() => void handlePurchase()}
              disabled={isPurchasing || loadingSummary || !!usernameTaken}
              className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all font-body font-semibold disabled:opacity-50"
            >
              {isPurchasing ? t('common.processing') : t('proxies.purchase.cta')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}